import React, { useState, useMemo } from 'react';
import WeeklyKPICards from './WeeklyKPICards';
import WeeklyActivityChart from './WeeklyActivityChart';
import TPLeaderboardChart from './TPLeaderboardChart';
import PipelineFunnelChart from './PipelineFunnelChart';
import OfferRateByClientChart from './OfferRateByClientChart';
import WeeklyReportsTable from './WeeklyReportsTable';

const BRAND_BLUE = '#4841F0';
const PERIOD_TABS = [
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'quarterly', label: 'Quarterly' },
];

const getMonthKey = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const getMonthLabel = (key) => {
  const [year, month] = key.split('-');
  return new Date(year, parseInt(month) - 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

const getQuarterKey = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  const q = Math.ceil((d.getMonth() + 1) / 3);
  return `${d.getFullYear()}-Q${q}`;
};
const getQuarterLabel = (key) => key.replace('-', ' ');

const WeeklyReports = ({ data = [], isLoading = false }) => {
  const [periodType, setPeriodType] = useState('weekly');
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [tpFilter, setTPFilter] = useState('');

  // --- Period options per type ---
  const availableWeeks = useMemo(() => {
    return [...new Set((data || []).map(r => r.weekNumber).filter(Boolean))].sort((a, b) => b - a);
  }, [data]);

  const availableMonths = useMemo(() => {
    return [...new Set((data || []).map(r => getMonthKey(r.timestamp)).filter(Boolean))].sort((a, b) => b.localeCompare(a));
  }, [data]);

  const availableQuarters = useMemo(() => {
    return [...new Set((data || []).map(r => getQuarterKey(r.timestamp)).filter(Boolean))].sort((a, b) => b.localeCompare(a));
  }, [data]);

  const activePeriods = periodType === 'weekly' ? availableWeeks : periodType === 'monthly' ? availableMonths : availableQuarters;
  const activePeriod = selectedPeriod ?? activePeriods[0] ?? null;

  // Reset period selection when switching tabs
  const handlePeriodTypeChange = (type) => {
    setPeriodType(type);
    setSelectedPeriod(null);
    setTPFilter('');
  };

  // --- Filter data for the active period ---
  const periodData = useMemo(() => {
    if (!activePeriod) return data;
    let filtered;
    if (periodType === 'weekly') {
      filtered = data.filter(r => r.weekNumber === activePeriod);
    } else if (periodType === 'monthly') {
      filtered = data.filter(r => getMonthKey(r.timestamp) === activePeriod);
    } else {
      filtered = data.filter(r => getQuarterKey(r.timestamp) === activePeriod);
    }
    if (tpFilter) filtered = filtered.filter(r => r.tpName === tpFilter);
    return filtered;
  }, [data, periodType, activePeriod, tpFilter]);

  const availableTPs = useMemo(() => {
    return [...new Set((data || []).map(r => r.tpName).filter(Boolean))].sort();
  }, [data]);

  const periodSelectLabel = periodType === 'weekly' ? 'Select Week' : periodType === 'monthly' ? 'Select Month' : 'Select Quarter';
  const periodOptionLabel = (p) => {
    if (periodType === 'weekly') {
      const rows = data.filter(r => r.weekNumber === p);
      if (rows.length > 0 && rows[0].timestamp) {
        const d = new Date(rows[0].timestamp);
        return `Week ${p} · ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
      }
      return `Week ${p}`;
    }
    if (periodType === 'monthly') return getMonthLabel(p);
    return getQuarterLabel(p);
  };

  const summaryText = periodType === 'weekly'
    ? `${data.length} submissions · ${availableWeeks.length} weeks`
    : periodType === 'monthly'
    ? `${data.length} submissions · ${availableMonths.length} months`
    : `${data.length} submissions · ${availableQuarters.length} quarters`;

  if (isLoading) {
    return (
      <div className="fade-in">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: BRAND_BLUE }} />
            <p className="text-sm text-gray-500">Loading TP reports...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="fade-in">
        <div className="dashboard-card p-10 text-center">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: `${BRAND_BLUE}15` }}>
            <svg className="w-7 h-7" style={{ color: BRAND_BLUE }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No TP Reports Yet</h3>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">
            TP reports will appear here once the Google Apps Script is updated to serve the <code className="bg-gray-100 px-1 rounded text-xs">weekly-reports</code> data type.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in space-y-6">
      {/* Header + period type tabs */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">TP Reports</h2>
          <p className="text-sm text-gray-500">{summaryText}</p>
        </div>

        {/* Weekly / Monthly / Quarterly tabs */}
        <div className="flex items-center bg-gray-100 rounded-lg p-1 gap-1">
          {PERIOD_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => handlePeriodTypeChange(tab.key)}
              className="px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-150"
              style={
                periodType === tab.key
                  ? { background: BRAND_BLUE, color: '#fff', boxShadow: '0 1px 3px rgba(72,65,240,0.25)' }
                  : { color: '#6b7280' }
              }
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Period + TP filters */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-600 font-medium whitespace-nowrap">{periodSelectLabel}:</label>
        <select
          value={activePeriod ?? ''}
          onChange={e => {
            const val = e.target.value;
            setSelectedPeriod(
              val === '' ? null : periodType === 'weekly' ? parseInt(val) : val
            );
          }}
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none shadow-sm"
        >
          {activePeriods.map(p => (
            <option key={p} value={p}>{periodOptionLabel(p)}</option>
          ))}
        </select>

        <div className="w-px h-6 bg-gray-300" />

        <label className="text-sm text-gray-600 font-medium whitespace-nowrap">Filter by TP:</label>
        <select
          value={tpFilter}
          onChange={e => setTPFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none shadow-sm"
        >
          <option value="">All TPs</option>
          {availableTPs.map(tp => (
            <option key={tp} value={tp}>{tp}</option>
          ))}
        </select>
      </div>

      {/* KPI Cards */}
      <WeeklyKPICards data={periodData} />

      {/* Charts grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <WeeklyActivityChart allData={periodData} />
        <TPLeaderboardChart data={periodData} />
        <PipelineFunnelChart data={periodData} />
        <OfferRateByClientChart data={periodData} />
      </div>

      {/* Submissions table */}
      <WeeklyReportsTable data={periodData} periodType={periodType} />
    </div>
  );
};

export default WeeklyReports;
